import {useCallback, useMemo, useState} from 'react';

import currencies from '@/constants/currencies';
import {AfriexCurrencyCode, AfriexWalletType} from '@/types';

import type {
  BalanceFilter,
  BalanceRow,
  BalanceSort,
  BusinessWalletBalances,
} from '../types';

const PAGE_SIZE = 10;

const currencyNames: Record<string, string> = Object.fromEntries(
  currencies.map((currency) => [currency.code, currency.name]),
);

const hasBalance = (row: BalanceRow) =>
  row.payout !== 0 || row.collection !== 0 || row.fee !== 0;

const owesFees = (row: BalanceRow) => row.fee < 0;

const sortRows = (rows: BalanceRow[], sort: BalanceSort): BalanceRow[] => {
  const sorted = [...rows];
  if (sort === 'code') {
    return sorted.sort((a, b) => a.code.localeCompare(b.code));
  }
  if (sort === 'payout') {
    return sorted.sort((a, b) => b.payout - a.payout);
  }
  return sorted.sort((a, b) => {
    if (owesFees(a) !== owesFees(b)) {
      return owesFees(a) ? -1 : 1;
    }
    return owesFees(a) ? a.fee - b.fee : b.payout - a.payout;
  });
};

/**
 * The server returns every currency's balance in one response, so search,
 * filter, sort and paging all work on the list already in memory.
 */
const useBusinessBalances = (walletBalances: BusinessWalletBalances) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filter, setFilter] = useState<BalanceFilter>('funded');
  const [sort, setSort] = useState<BalanceSort>('debt');
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);

  const rows = useMemo<BalanceRow[]>(
    () =>
      Object.values(AfriexCurrencyCode).map((code) => ({
        code,
        name: currencyNames[code] ?? '',
        payout: walletBalances?.[AfriexWalletType.Withdraw]?.[code] ?? 0,
        collection: walletBalances?.[AfriexWalletType.Deposit]?.[code] ?? 0,
        fee: walletBalances?.[AfriexWalletType.Fee]?.[code] ?? 0,
      })),
    [walletBalances],
  );

  const counts = useMemo<Record<BalanceFilter, number>>(
    () => ({
      funded: rows.filter(hasBalance).length,
      owing: rows.filter(owesFees).length,
      all: rows.length,
    }),
    [rows],
  );

  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matching = rows.filter((row) => {
      if (filter === 'funded' && !hasBalance(row)) {
        return false;
      }
      if (filter === 'owing' && !owesFees(row)) {
        return false;
      }
      return (
        !term ||
        row.code.toLowerCase().includes(term) ||
        row.name.toLowerCase().includes(term)
      );
    });
    return sortRows(matching, sort);
  }, [rows, filter, searchTerm, sort]);

  const list = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, page]);

  const resetPage = () => {
    setPage(0);
    setIsPageReset((prev) => !prev);
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
    resetPage();
  }, []);

  const handleFilter = useCallback((value: BalanceFilter) => {
    setFilter(value);
    resetPage();
  }, []);

  const handleSort = useCallback((value: BalanceSort) => {
    setSort(value);
    resetPage();
  }, []);

  return {
    list,
    total: filteredList.length,
    counts,
    searchTerm,
    handleSearch,
    filter,
    handleFilter,
    sort,
    handleSort,
    setPage,
    isPageReset,
    isSearch: Boolean(searchTerm.trim()),
  };
};

export default useBusinessBalances;
